import { useEffect, useState, useMemo } from "react";
import { api } from "../../api";
import { useLanguage } from "../../contexts/LanguageContext";
import { financialLabels } from "./labels";
import { 
  Calendar,
  ChevronRight,
  ChevronLeft,
  Download,
} from "../../components/icons";
import { formatDate } from "../utils/date";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AddExpenseDialog } from "./components/AddExpenseDialog";
import { AddIncomeDialog } from "./components/AddIncomeDialog";
import { InsuranceBatchDrawer } from "./components/InsuranceBatchDrawer";
import { FinancialSummary } from "./components/FinancialSummary";
import { FinancialTable } from "./components/FinancialTable";
import { InsurancePendingCard } from "./components/FinancialSummary";

export default function FinancialPage() {
  const { lang } = useLanguage();
  const t = financialLabels[lang];

  // Generate month options: Next Month + Current + Last 10 months
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    // Add Next Month
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    options.push({
      label: nextMonth.toLocaleDateString(lang === 'pt' ? 'pt-BR' : 'en-US', { month: 'long', year: 'numeric' }),
      value: `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`
    });
    // Add Current and Past
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        label: d.toLocaleDateString(lang === 'pt' ? 'pt-BR' : 'en-US', { month: 'long', year: 'numeric' }),
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      });
    }
    return options;
  }, [lang]);

  const [activeTab, setActiveTab] = useState<'incomes' | 'expenses' | 'all' | 'insurance'>('all');
  const [period, setPeriod] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [payments, setPayments] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [specialties, setSpecialties] = useState<any[]>([]);
  const [insuranceProviders, setInsuranceProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "paid" | "pending" | "insurance_pending">("all");
  const [showBatchDialog, setShowBatchDialog] = useState(false);

  const handlePrevMonth = () => {
    const [y, m] = period.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleNextMonth = () => {
    const [y, m] = period.split('-').map(Number);
    const d = new Date(y, m, 1);
    setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const load = async () => {
    setLoading(true);
    const [payRes, expRes, specRes, insRes] = await Promise.all([
      api.listPayments(),
      api.listExpenses(),
      api.listCompanySpecialties(),
      api.listInsuranceProviders()
    ]);
    if (payRes.ok) setPayments(payRes.data || []);
    if (expRes.ok) setExpenses(expRes.data || []);
    if (specRes.ok) setSpecialties(specRes.data || []);
    if (insRes.ok) setInsuranceProviders(insRes.data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const isWithinPeriod = (dateStr: string) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const [year, month] = period.split('-').map(Number);
    return date.getFullYear() === year && (date.getMonth() + 1) === month;
  };

  const currentData = useMemo(() => {
    const [selYear, selMonth] = period.split('-').map(Number);
    
    // Process expenses expansion
    const expandedExpenses: any[] = [];
    expenses.forEach(e => {
      const dueDate = new Date(e.due_date);
      const startYear = dueDate.getFullYear();
      const startMonth = dueDate.getMonth() + 1;
      
      const baseExpense = {
        ...e,
        is_expense: true,
        display_name: e.description,
        display_date: e.due_date,
        amount_cents: e.amount_cents
      };

      if (startYear === selYear && startMonth === selMonth) {
        expandedExpenses.push(baseExpense);
      } else if (e.is_recurring) {
        const isStarted = (startYear < selYear) || (startYear === selYear && startMonth < selMonth);
        const isNotEnded = !e.recurrence_end_at || new Date(e.recurrence_end_at) > new Date(selYear, selMonth - 1, 1);
        if (isStarted && isNotEnded) {
          expandedExpenses.push({
            ...baseExpense,
            id: `v-${e.id}-${period}`,
            original_id: e.id,
            due_date: new Date(selYear, selMonth - 1, dueDate.getDate()).toISOString(),
            display_date: new Date(selYear, selMonth - 1, dueDate.getDate()).toISOString(),
            status: 'pending',
            is_virtual: true
          });
        }
      }
    });

    const processedPayments = payments.map(p => ({ 
      ...p, 
      is_income: true,
      display_name: p.client_name,
      display_date: p.due_date || p.paid_at || p.created_at,
      amount_cents: p.total_amount_cents
    }));

    if (activeTab === 'incomes') return processedPayments;
    if (activeTab === 'expenses') return expandedExpenses;
    
    return [...processedPayments, ...expandedExpenses].sort((a, b) => {
      const dateA = new Date(a.display_date).getTime();
      const dateB = new Date(b.display_date).getTime();
      return dateB - dateA;
    });
  }, [payments, expenses, activeTab, period]);

  const filtered = useMemo(() => {
    return currentData.filter(p => {
      if (!isWithinPeriod(p.display_date)) return false;
      const name = p.display_name;
      const matchesSearch = name?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter || (statusFilter === 'insurance_pending' && p.status === 'insurance_pending');
      return matchesSearch && matchesStatus;
    });
  }, [currentData, search, statusFilter, period]);

  const handleMarkAsPaid = async (p: any) => {
    const newStatus = p.status === 'paid' ? 'pending' : 'paid';
    const res = p.is_income 
      ? await api.updatePayment(p.id, { status: newStatus })
      : await api.updateExpense(p.original_id || p.id, { status: newStatus });

    if (res.ok) {
      toast.success(lang === 'pt' ? 
        (newStatus === 'paid' ? "Confirmado" : "Desmarcado") : 
        (newStatus === 'paid' ? "Confirmed" : "Unmarked")
      );
      if (p.is_income) {
        setPayments(prev => prev.map(item => 
          item.id === p.id ? { ...item, ...res.data } : item
        ));
      } else {
        setExpenses(prev => prev.map(item => 
          (item.id === p.id || item.id === p.original_id) ? { ...item, status: newStatus } : item
        ));
      }
    }
  };

  const handleDelete = async (p: any) => {
    const res = p.is_income 
      ? await api.deletePayment(p.id)
      : await api.deleteExpense(p.original_id || p.id);

    if (res.ok) {
      toast.success(lang === 'pt' ? "Excluído" : "Deleted");
      if (p.is_income) {
        setPayments(prev => prev.filter(item => item.id !== p.id));
      } else {
        const idToDelete = p.original_id || p.id;
        setExpenses(prev => prev.filter(item => item.id !== idToDelete));
      }
    }
  };

  const pendingInsurancePayments = useMemo(() => {
    return payments.filter(p => 
      (p.status === 'insurance_pending' || p.status === 'pending') &&
      p.insurance_provider_id && 
      p.insurance_provider_id !== 1
    );
  }, [payments]);

  const handleExportCSV = () => {
    const headers = [
      lang === 'pt' ? "Descrição" : "Description",
      lang === 'pt' ? "Tipo" : "Type",
      lang === 'pt' ? "Valor" : "Amount",
      lang === 'pt' ? "Data" : "Date",
      lang === 'pt' ? "Status" : "Status",
      lang === 'pt' ? "Método" : "Method"
    ];

    const rows = filtered.map(p => [
      p.display_name,
      p.is_income ? (p.type === 'monthly' ? t.types.monthly : t.types.per_consultation) : (p.category || "Despesa"),
      (p.amount_cents / 100).toFixed(2),
      formatDate(p.display_date),
      p.status === 'paid' ? t.filters.paid : p.status === 'insurance_pending' ? t.filters.insurance_pending : t.filters.pending,
      p.payment_method || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `financial_${period}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleStopRecurrence = async (id: number) => {
    const now = new Date();
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString();
    
    const res = await api.updateExpense(id, { recurrence_end_at: lastDayOfMonth });
    if (res.ok) {
      toast.success(lang === 'pt' ? "Recorrência interrompida" : "Recurrence stopped");
      setExpenses(prev => prev.map(item => 
        item.id === id ? { ...item, recurrence_end_at: lastDayOfMonth } : item
      ));
    }
  };

  const handleUpdateAmount = async (item: any, newAmountCents: number) => {
    try {
      let res;
      if (item.is_income) {
        res = await api.updatePayment(item.id, { total_amount_cents: newAmountCents });
      } else {
        const expenseId = item.original_id || item.id;
        res = await api.updateExpense(expenseId, { amount_cents: newAmountCents });
      }

      if (res.ok) {
        if (item.is_income) {
          setPayments(prev => prev.map(p => 
            p.id === item.id ? { ...p, total_amount_cents: newAmountCents, amount_cents: newAmountCents } : p
          ));
        } else {
          setExpenses(prev => prev.map(e => 
            (e.id === item.id || e.id === item.original_id) ? { ...e, amount_cents: newAmountCents } : e
          ));
        }
        toast.success(lang === 'pt' ? "Valor atualizado" : "Amount updated");
      } else {
        throw new Error(res.error || (lang === 'pt' ? "Erro ao atualizar" : "Error updating"));
      }
    } catch (err: any) {
      toast.error(err.message || (lang === 'pt' ? "Erro ao atualizar" : "Error updating"));
    }
  };

  const stats = useMemo(() => {
    const pFiltered = payments.filter(p => isWithinPeriod(p.paid_at || p.created_at));
    const [selYear, selMonth] = period.split('-').map(Number);
    const eExpanded: any[] = [];
    expenses.forEach(e => {
      const dueDate = new Date(e.due_date);
      const startYear = dueDate.getFullYear();
      const startMonth = dueDate.getMonth() + 1;
      if (startYear === selYear && startMonth === selMonth) {
        eExpanded.push(e);
      } else if (e.is_recurring) {
        const isStarted = (startYear < selYear) || (startYear === selYear && startMonth < selMonth);
        const isNotEnded = !e.recurrence_end_at || new Date(e.recurrence_end_at) > new Date(selYear, selMonth - 1, 1);
        if (isStarted && isNotEnded) {
          eExpanded.push({ ...e, status: 'pending' });
        }
      }
    });

    const totalIncomes = pFiltered.reduce((acc, p) => p.status === 'paid' ? acc + p.total_amount_cents : acc, 0);
    const totalExpenses = eExpanded.reduce((acc, p) => p.status === 'paid' ? acc + p.amount_cents : acc, 0);
    
    const pendingIncomes = pFiltered.reduce((acc, p) => p.status === 'pending' ? acc + p.total_amount_cents : acc, 0);
    const pendingExpenses = eExpanded.reduce((acc, p) => p.status === 'pending' ? acc + p.amount_cents : acc, 0);

    return {
      incomes: totalIncomes,
      expenses: totalExpenses,
      balance: totalIncomes - totalExpenses,
      projection: (totalIncomes + pendingIncomes) - (totalExpenses + pendingExpenses),
      pendingIncomes,
      pendingExpenses
    };
  }, [payments, expenses, period]);

  const resolveSpecialtyName = (p: any) => {
    if (p.specialty_id) {
      const spec = specialties.find(s => s.id === p.specialty_id);
      if (spec) return spec.name;
    }
    if (p.plan_name) {
      const match = specialties.find(s => 
        p.plan_name.toLowerCase().includes(s.name.toLowerCase()) ||
        s.name.toLowerCase().includes(p.plan_name.toLowerCase())
      );
      if (match) return match.name;
      return p.plan_name;
    }
    return p.category || (lang === 'pt' ? "Outros" : "Others");
  };

  const dynamicBreakdown = useMemo(() => {
    const groups: Record<string, number> = {};
    payments
      .filter(p => isWithinPeriod(p.paid_at || p.created_at) && p.status === 'paid')
      .forEach(p => {
        const key = resolveSpecialtyName(p);
        groups[key] = (groups[key] || 0) + p.total_amount_cents;
      });
    return Object.entries(groups).sort((a, b) => b[1] - a[1]);
  }, [payments, specialties, period, lang]);

  const methodBreakdown = useMemo(() => {
    const methods = ['Pix', 'Cartão', 'Dinheiro', 'Transferência'];
    return methods.map(method => ({
      method,
      total: payments
        .filter(p => p.payment_method === method && isWithinPeriod(p.paid_at || p.created_at) && p.status === 'paid')
        .reduce((acc, p) => acc + p.total_amount_cents, 0)
    })).filter(m => m.total > 0);
  }, [payments, period]);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 p-4">
      <InsurancePendingCard
        lang={lang}
        pendingInsuranceCount={pendingInsurancePayments.length}
        onOpenBatchDrawer={() => setShowBatchDialog(true)}
        t={t}
      />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight text-neutral-900">{t.title}</h1>
          <div className="flex items-center gap-1 mt-2">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full hover:bg-neutral-100" onClick={handlePrevMonth}>
              <ChevronLeft className="h-4 w-4 text-neutral-500" />
            </Button>
            
            <Select value={period} onValueChange={(val: any) => setPeriod(val)}>
              <SelectTrigger className="h-8 w-[180px] rounded-full bg-neutral-50 text-[10px] font-bold uppercase tracking-wider border-neutral-200">
                <Calendar className="mr-2 h-3 w-3 text-neutral-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full hover:bg-neutral-100" onClick={handleNextMonth}>
              <ChevronRight className="h-4 w-4 text-neutral-500" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="h-9 font-bold text-neutral-600 border-neutral-200"
            onClick={handleExportCSV}
          >
            <Download className="mr-2 h-4 w-4" />
            {t.actions.exportCsv}
          </Button>
          <AddIncomeDialog onSuccess={load} />
          <AddExpenseDialog onSuccess={load} />
        </div>
      </div>

      <FinancialSummary
        lang={lang}
        stats={stats}
        pendingInsuranceCount={pendingInsurancePayments.length}
        onOpenBatchDrawer={() => setShowBatchDialog(true)}
        dynamicBreakdown={dynamicBreakdown}
        methodBreakdown={methodBreakdown}
        t={t}
      />

      <FinancialTable
        lang={lang}
        t={t}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        search={search}
        setSearch={setSearch}
        loading={loading}
        filteredItems={filtered}
        insuranceProviders={insuranceProviders}
        resolveSpecialtyName={resolveSpecialtyName}
        handleMarkAsPaid={handleMarkAsPaid}
        handleDelete={handleDelete}
        handleStopRecurrence={handleStopRecurrence}
        onEditAmount={handleUpdateAmount}
      />

      <InsuranceBatchDrawer
        open={showBatchDialog}
        onOpenChange={setShowBatchDialog}
        lang={lang}
        insuranceProviders={insuranceProviders}
        pendingInsurancePayments={pendingInsurancePayments}
        onSuccess={load}
      />
    </div>
  );
}
